import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {User} from '../auth/user.model';
import {AuthService} from '../auth/auth.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MixpanelService } from 'src/app/shared/services/mixpanel.service';
import { MixpanelEvents } from 'src/app/shared/analytics/mixpanel-events';
import { trackMixpanelFailure } from 'src/app/shared/analytics/mixpanel-tracking.utils';

@Injectable()
export class LoginService {

  constructor(private authService: AuthService,
              private router: Router,
              private httpClient: HttpClient,
              private mixpanel: MixpanelService) {
  }

  isLoggedIn() {
    return this.authService.isAuthenticated();
  }

  login(credentials: User) {
    return new Promise((resolve, reject) => {
      this.authService.login(credentials).subscribe((user: User) => {
        if (user?.email) {
          this.mixpanel.identify(user.email);
          this.mixpanel.track(MixpanelEvents.LoginSucceeded, { auth_method: 'password' });
        }
        resolve(user);
      }, (err) => {
        trackMixpanelFailure(this.mixpanel, MixpanelEvents.LoginFailed, { auth_method: 'password' }, err);
        this.logout();
        reject(err);
      });
    });
  }

  logout(navigateToLogin = false) {
    this.endMixpanelSessionIfAuthenticated();
    this.authService.logout().subscribe();
    if (navigateToLogin) {
      this.router.navigate(['/login']);
    }
  }

  public forgotPasswordMail(email: string): Observable<any> {
    return this.httpClient.post(`${environment.serviceUrl}/api/users/password-recovery/send-email`, email, {
      responseType: 'text',
    });
  }

  public resetPassword(recoveryToken: string, newPassword: string, confirmPassword: string): Observable<any> {
    return this.httpClient.put(`${environment.serviceUrl}/api/users/password-recovery/change-password/${recoveryToken}`, {newPassword, confirmPassword}, {
      responseType: 'text',
    });
  }

  public changePassword(data): Observable<any> {
    return this.httpClient.put(`${environment.serviceUrl}/api/users/change-password`, data, {
      responseType: 'text',
    });
  }

  public isRecoveryTokenActive(recoveryToken: string): Observable<any> {
    return this.httpClient.get(`${environment.serviceUrl}/api/users/password-recovery/validate/${recoveryToken}`, {
      responseType: 'text',
    });
  }

  logoutWithReturnUrl(returnUrl) {
    this.endMixpanelSessionIfAuthenticated();
    this.authService.logout().subscribe();
    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl
      }
    });
  }

  /**
   * One Logout event per real session: interceptor may call logout again with no token after reset.
   */
  private endMixpanelSessionIfAuthenticated(): void {
    if (!this.authService.getToken()) {
      return;
    }
    this.mixpanel.track(MixpanelEvents.Logout);
    this.mixpanel.reset();
  }
}
